import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { fabric } from 'fabric';
import PropTypes from 'prop-types';
import PolygonIcon from '../images/Icon20_Polygon.svg';
import Button from './Toolbar/Button';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import { getThicknessPx, hexToRgba } from '../helpers/fabricHelpers';

import { __ } from '../../../core/utils';

const Polygon = ({
  canvas_editor: {
    activeTool,
    viewer,
    fabricOverlay,
    color,
    selectedThickness,
  },
  source,
  isActive,
  user_id,
  user_full_name,
}) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    points: [],
    lines: [],
    previewLine: null,
  });

  useEffect(() => {
    if (!fabricOverlay || !isActive) return;

    const canvas = fabricOverlay.fabricCanvas();
    canvasRef.current = canvas;
    canvas.selection = false;
    canvas.defaultCursor = 'crosshair';

    const DOUBLE_TAP_DELAY = 400;
    let lastTouchTime = 0;

    setCursorTooltip(__('canvas.tooltip.polygon.start'));

    canvas.getObjects().forEach((obj) => {
      obj.selectable = false;
      obj.evented = false;
    });

    const handleClick = (options) => {
      if (options.target) return;

      const raw = canvas.getPointer(options.e);
      const pointer = {
        x: Math.max(0, Math.min(source.width, raw.x)),
        y: Math.max(0, Math.min(source.height, raw.y)),
      };

      const { points, lines, previewLine } = stateRef.current;

      if (points.length > 0) {
        const last = points[points.length - 1];
        const newLine = new fabric.Line(
          [last.x, last.y, pointer.x, pointer.y],
          {
            stroke: color.hex,
            strokeWidth: getThicknessPx(selectedThickness.key),
            selectable: false,
            evented: false,
            objectType: 'controlpoint',
            isTemporary: true,
            //objectCaching: false,
          }
        );
        canvas.add(newLine);
        lines.push(newLine);
      }

      points.push(pointer);

      if (previewLine) {
        canvas.remove(previewLine);
        stateRef.current.previewLine = null;
      }

      viewer.setMouseNavEnabled(true);
    };

    const handleMouseMove = (options) => {
      const { points, previewLine } = stateRef.current;
      if (points.length === 0) return;

      const raw = canvas.getPointer(options.e);
      const pointer = {
        x: Math.max(0, Math.min(source.width, raw.x)),
        y: Math.max(0, Math.min(source.height, raw.y)),
      };

      const last = points[points.length - 1];

      if (!previewLine) {
        const line = new fabric.Line([last.x, last.y, pointer.x, pointer.y], {
          stroke: color.hex,
          strokeWidth: getThicknessPx(selectedThickness.key),
          selectable: false,
          evented: false,
          objectType: 'controlpoint',
          isTemporary: true,
        });
        stateRef.current.previewLine = line;
        canvas.add(line);
      } else {
        previewLine.set({
          x1: last.x,
          y1: last.y,
          x2: pointer.x,
          y2: pointer.y,
        });
        previewLine.setCoords();
      }

      canvas.requestRenderAll();

      setCursorTooltip(__('canvas.tooltip.polygon.drawing'));
    };

    const handleDblClick = () => {
      const { points, lines, previewLine } = stateRef.current;

      if (points.length < 3) return;

      const polygon = new fabric.Polygon(points, {
        fill: hexToRgba(color.hex, 0.05),
        stroke: color.hex,
        strokeWidth: getThicknessPx(selectedThickness.key),
        objectType: 'polygon',
        selectable: false,
        hoverCursos: 'default',
        evented: false,
        userID: user_id,
        userFullName: user_full_name,
      });

      lines.forEach((line) => canvas.remove(line));
      if (previewLine) canvas.remove(previewLine);

      canvas.add(polygon);

      stateRef.current = {
        points: [],
        lines: [],
        previewLine: null,
      };

      updateTool({ tool: 'hand' });

      //setCursorTooltip(__('canvas.tooltip.polygon.start'));
    };

    const onPointerDown = (e) => {
      viewer.setMouseNavEnabled(false);
    };

    const detectDoubleTap = (e) => {
      const currentTime = Date.now();
      if (currentTime - lastTouchTime < DOUBLE_TAP_DELAY) {
        handleDblClick();
        lastTouchTime = 0;
      } else {
        lastTouchTime = currentTime;
      }
    };

    canvas.on('mouse:down', handleClick);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:dblclick', handleDblClick);
    canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
    canvas.upperCanvasEl.addEventListener('touchend', detectDoubleTap);

    return () => {
      canvas.off('mouse:down', handleClick);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:dblclick', handleDblClick);

      canvas.upperCanvasEl.removeEventListener('pointerdown', onPointerDown);
      canvas.upperCanvasEl.removeEventListener('touchend', detectDoubleTap);

      // clean up
      const { lines, previewLine } = stateRef.current;
      lines.forEach((line) => canvas.remove(line));
      if (previewLine) canvas.remove(previewLine);
      stateRef.current = { points: [], lines: [], previewLine: null };
    };
  }, [fabricOverlay, isActive, source, color, selectedThickness]);

  const toggleTool = () => {
    updateTool({ tool: 'polygon' });
  };

  return (
    <Button
      id='polygon'
      label={__('canvas.toolbar.polygon')}
      icon={PolygonIcon}
      active={activeTool === 'polygon'}
      onClick={toggleTool}
    />
  );
};

Polygon.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps)(Polygon);
